import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../hooks/useNotifications";
import { inAppNotificationService } from "../services/inAppNotificationService";
import { NotificationData } from "../types/notification";
import { invitationService } from "../services/invitationService";
import { Invitation } from "../types/invitation";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/layout/Header";

const Notifications = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [combinedNotifications, setCombinedNotifications] = useState<
    NotificationData[]
  >([]);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [hideRead, setHideRead] = useState(false);

  const {
    notifications,
    counts,
    loading,
    backgroundLoading,
    error,
    isFromCache,
    isOffline,
    lastUpdated,
    refresh,
    markAsRead,
  } = useNotifications();

  // Fetch invitations when component mounts or user changes
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!currentUser?.email) {
        setInvitationsLoading(false);
        return;
      }

      try {
        setInvitationsLoading(true);
        const userInvitations = await invitationService.getInvitations(
          currentUser.email,
        );
        // Only show pending invitations
        const pendingInvitations = userInvitations.filter(
          (inv) => inv.status === "pending",
        );
        setInvitations(pendingInvitations);
      } catch (error) {
        console.error("Error fetching invitations:", error);
        setInvitations([]);
      } finally {
        setInvitationsLoading(false);
      }
    };

    fetchInvitations();
  }, [currentUser?.email]);

  // Combine and sort notifications with invitations
  useEffect(() => {
    // Convert invitations to notification format
    const invitationNotifications: NotificationData[] = invitations.map(
      (invitation) => ({
        id: `invitation-${invitation.id}`,
        type: "invitation" as any,
        message:
          invitation.message ||
          `${invitation.coachName || invitation.coachEmail} has invited you to join their coaching program`,
        actionText: "View Invitation",
        actionEndpoint: `/invitations`,
        read: invitation.status !== "pending",
        timestamp: {
          seconds: Math.floor(
            invitation.createdAt.getTime
              ? invitation.createdAt.getTime() / 1000
              : (invitation.createdAt as any).seconds || 0,
          ),
          nanoseconds: 0,
        },
        imageUrl: undefined,
        userId: currentUser?.uid || "",
        title: "Coach Invitation",
      }),
    );

    // Combine all notifications
    const allNotifications = [...notifications, ...invitationNotifications];

    // Sort by timestamp (most recent first)
    const sorted = allNotifications.sort((a, b) => {
      const aTime = a.timestamp.seconds;
      const bTime = b.timestamp.seconds;
      return bTime - aTime;
    });

    setCombinedNotifications(sorted);
  }, [notifications, invitations, currentUser?.uid]);

  const handleNotificationClick = async (notification: NotificationData) => {
    try {
      // Mark as read if not already read
      if (!notification.read && !notification.id.startsWith("invitation-")) {
        await markAsRead(notification.id);
      }

      // Navigate to the action endpoint for regular notifications
      inAppNotificationService.handleNotificationAction(
        notification.actionEndpoint,
        navigate,
      );
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const LoadingNotificationCard = () => (
    <div className="p-4 border-b border-base-300 bg-blue-500/10 border-l-4 border-l-blue-500 animate-pulse">
      <div className="flex items-start space-x-3">
        {/* Loading Icon */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
            <div className="w-4 h-4 bg-blue-400 rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Loading Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="h-4 bg-blue-200 rounded w-1/4 animate-pulse"></div>
            <div className="h-3 bg-blue-200 rounded w-16 animate-pulse"></div>
          </div>
          <div className="h-3 bg-blue-200 rounded w-3/4 mt-2 animate-pulse"></div>
          <div className="flex items-center justify-between mt-2">
            <div className="h-6 bg-blue-200 rounded-full w-24 animate-pulse"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const NotificationCard = ({
    notification,
  }: {
    notification: NotificationData;
  }) => (
    <div
      key={notification.id}
      className={`p-4 border-b border-base-300 cursor-pointer transition-colors hover:bg-base-200 ${
        !notification.read
          ? "bg-blue-500/10 border-l-4 border-l-blue-500"
          : "bg-base-100"
      }`}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="flex items-start space-x-3 px-2">
        {/* Notification Icon */}
        <div className="flex-shrink-0">
          {notification.imageUrlTiny || notification.imageUrl ? (
            <img
              src={notification.imageUrlTiny || notification.imageUrl}
              alt="Notification"
              className="w-10 h-10 rounded-full"
            />
          ) : (
            <div className="w-10 h-10 bg-blue-500/15 rounded-full flex items-center justify-center text-xl">
              {inAppNotificationService.getNotificationIcon(notification.type)}
            </div>
          )}
        </div>

        {/* Notification Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-base-content truncate">
              {notification.id.startsWith("invitation-")
                ? "Coach Invitation"
                : inAppNotificationService.getNotificationTypeName(
                    notification.type,
                  )}
            </p>
            <p className="text-xs text-base-content/60 flex-shrink-0">
              {(() => {
                return inAppNotificationService.formatNotificationTime(
                  notification.timestamp,
                );
              })()}
            </p>
          </div>
          <p className="text-sm text-base-content/80 mt-1">
            {notification.message}
          </p>
          <div className="flex items-center justify-between mt-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-500/15 text-base-content/60">
              {notification.actionText}
            </span>
            {!notification.read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    // Check if it's an offline error with no cached data
    const isOfflineError = error.includes(
      "No internet connection and no cached data available",
    );

    return (
      <div className="">
        <h2 className="text-2xl font-semibold text-base-content mb-4">
          Notifications
        </h2>
        <div
          className={`border rounded-md p-4 ${
            isOfflineError
              ? "bg-yellow-500/10 border-yellow-200"
              : "bg-red-500/10 border-red-200"
          }`}
        >
          <div className="flex">
            <div
              className={isOfflineError ? "text-yellow-400" : "text-red-400"}
            >
              {isOfflineError ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3
                className={`text-sm font-medium ${
                  isOfflineError
                    ? "text-yellow-700 dark:text-yellow-400"
                    : "text-red-700 dark:text-red-400"
                }`}
              >
                {isOfflineError
                  ? "You're offline"
                  : "Error loading notifications"}
              </h3>
              <p
                className={`text-sm mt-1 ${
                  isOfflineError ? "text-yellow-700" : "text-red-700"
                }`}
              >
                {isOfflineError
                  ? "No cached notifications available. Connect to the internet to view your notifications."
                  : error}
              </p>
              {!isOfflineError && (
                <button
                  onClick={refresh}
                  className="mt-2 bg-red-500/15 hover:bg-red-200 text-red-700 dark:text-red-400 px-3 py-1 rounded text-sm font-medium transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-16">
      <Header />

      <div className="p-0 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="px-6 pt-8">
            <h2 className="text-2xl font-semibold text-base-content">
              Notifications
            </h2>
            <div className="mt-1">
              <p className="text-sm text-base-content/70">
                {counts.total === 0
                  ? "No notifications yet"
                  : counts.unread > 0 || invitations.length > 0
                    ? `${counts.total + invitations.length} total notifications • ${counts.unread + invitations.length} unread`
                    : `${counts.total} total notifications • All read`}
              </p>
            </div>
            {counts.total - counts.unread > 0 && (
              <button
                onClick={() => setHideRead((prev) => !prev)}
                className={`inline-flex items-center py-2  text-sm font-medium transition-colors ${
                  hideRead ? "  " : " text-base-content/50 "
                }`}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      hideRead
                        ? "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        : "M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                    }
                  />
                </svg>
                {hideRead ? "Show All" : "Clear Read"}
              </button>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex space-x-3 pr-4">
            <button
              onClick={refresh}
              className="inline-flex items-center px-3 py-2 border border-base-300 rounded-md text-sm font-medium text-base-content/60 hover:bg-base-200 transition-colors"
              disabled={loading}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Notifications List */}
        {combinedNotifications.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-base-content mb-2">
              No notifications yet
            </h3>
          </div>
        ) : (
          <div className="bg-base-100 rounded-lg shadow overflow-hidden">
            {/* Show loading card when fetching new data and there are unread notifications */}
            {backgroundLoading && <LoadingNotificationCard />}
            {combinedNotifications
              .filter((n) => !hideRead || !n.read)
              .map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
